import {
  json,
  redirect,
  type MetaFunction,
  type ActionFunction,
  type AppLoadContext,
  type LoaderArgs,
} from '@shopify/remix-oxygen';
import {Form, useActionData, useLoaderData, useMatches} from '@remix-run/react';
import {useState} from 'react';
import {getInputStyleClasses} from '~/lib/utils';
import {Link} from '~/components';
import type {CustomerAccessTokenCreatePayload} from '@shopify/hydrogen/storefront-api-types';

//components
import AccountWrapper from 'components/account/AccountWrapper';
import AccountBreadcrumb from 'components/account/AccountBreadcrumb';

//translations
import translation from 'translations/login.json';
import type {routeLocale} from 'domains/global';

export const handle = {
  isPublic: true,
};

export async function loader({context, params}: LoaderArgs) {
  const customerAccessToken = await context.session.get('customerAccessToken');

  if (customerAccessToken) {
    return redirect(params.lang ? `/${params.lang}/account` : '/account');
  }

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  // TODO: Query for this?
  return json({
    shopName: 'Helmet King',
    translation: translation[selectedLocale],
  });
}

type ActionData = {
  formError?: string;
};

const badRequest = (data: ActionData) => json(data, {status: 400});

export const action: ActionFunction = async ({request, context, params}) => {
  const formData = await request.formData();

  const email = formData.get('email');
  const password = formData.get('password');

  if (
    !email ||
    !password ||
    typeof email !== 'string' ||
    typeof password !== 'string'
  ) {
    return badRequest({
      formError: 'Please provide both an email and a password.',
    });
  }

  const {session, storefront} = context;

  try {
    const customerAccessToken = await doLogin(context, {email, password});
    session.set('customerAccessToken', customerAccessToken);

    return redirect(params.lang ? `/${params.lang}/account` : '/account', {
      headers: {
        'Set-Cookie': await session.commit(),
      },
    });
  } catch (error: any) {
    if (storefront.isApiError(error)) {
      return badRequest({
        formError: 'Something went wrong. Please try again later.',
      });
    }

    /**
     * The user did something wrong, but the raw error from the API is not super friendly.
     * Let's make one up.
     */
    return badRequest({
      formError:
        'Sorry. We did not recognize either your email or password. Please try to sign in again or create a new account.',
    });
  }
};

export const meta: MetaFunction = () => {
  return {
    title: 'Login',
  };
};

export default function Login() {
  const {shopName, translation} = useLoaderData<typeof loader>();
  const actionData = useActionData<ActionData>();
  const [nativeEmailError, setNativeEmailError] = useState<null | string>(null);
  const [nativePasswordError, setNativePasswordError] = useState<null | string>(
    null,
  );

  const [root] = useMatches();

  return (
    <>
      <AccountBreadcrumb
        currentPageLabel={translation.sign_in ?? 'Sign In'}
        homePageLabel={translation.home ?? 'Home'}
      />
      <AccountWrapper>
        {/* TODO: Add onSubmit to validate _before_ submission with native? */}
        <Form
          method="post"
          noValidate
          className="pt-6 pb-8 mt-4 mb-4 space-y-3"
        >
          {actionData?.formError && (
            <div className="mb-6 flex items-center justify-center rounded-sm border border-red p-4 text-sm text-red">
              <p>{translation.sign_in_error_message ?? actionData.formError}</p>
            </div>
          )}
          <div>
            <input
              className={`mb-1 ${getInputStyleClasses(nativeEmailError)}`}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={translation.email_label ?? 'Email address'}
              aria-label={translation.email_label ?? 'Email address'}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onBlur={(event) => {
                setNativeEmailError(
                  event.currentTarget.value.length &&
                    !event.currentTarget.validity.valid
                    ? 'Invalid email address'
                    : null,
                );
              }}
            />
            {nativeEmailError && (
              <p className="text-red-500 text-xs">{nativeEmailError} &nbsp;</p>
            )}
          </div>

          <div>
            <input
              className={`mb-1 ${getInputStyleClasses(nativePasswordError)}`}
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder={translation.password_label ?? 'Password'}
              aria-label={translation.password_label ?? 'Password'}
              minLength={8}
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onBlur={(event) => {
                if (
                  event.currentTarget.validity.valid ||
                  !event.currentTarget.value.length
                ) {
                  setNativePasswordError(null);
                } else {
                  setNativePasswordError(
                    event.currentTarget.validity.valueMissing
                      ? 'Please enter a password'
                      : 'Passwords must be at least 8 characters',
                  );
                }
              }}
            />
            {nativePasswordError && (
              <p className="text-red-500 text-xs">
                {' '}
                {nativePasswordError} &nbsp;
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <button
              className="flex h-[2.5rem] items-center justify-center overflow-hidden p-4 text-sm font-bold duration-200 ease-out disabled:opacity-20 disabled:bg-opacity-100 hover:opacity-80 w-full rounded bg-hk-orange text-white"
              type="submit"
              disabled={!!(nativePasswordError || nativeEmailError)}
            >
              {translation.sign_in ?? 'Sign in'}
            </button>
          </div>
          <div className="flex justify-between mt-4">
            <p className="text-sm">
              {translation.new_user ?? 'New user?'}{' '}
              <Link className="inline underline" to="/account/register">
                {translation.create_an_account ?? 'Create an account'}
              </Link>
            </p>

            <p className="text-sm">
              <Link
                className="text-black/50 inline-block align-baseline text-sm"
                to="/account/recover"
              >
                {translation.forgot_password ?? 'Forgot password?'}
              </Link>
            </p>
          </div>
        </Form>
      </AccountWrapper>
    </>
  );
}

const LOGIN_MUTATION = `#graphql
  mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerUserErrors {
        code
        field
        message
      }
      customerAccessToken {
        accessToken
        expiresAt
      }
    }
  }
`;

export async function doLogin(
  {storefront}: AppLoadContext,
  {
    email,
    password,
  }: {
    email: string;
    password: string;
  },
) {
  const data = await storefront.mutate<{
    customerAccessTokenCreate: CustomerAccessTokenCreatePayload;
  }>(LOGIN_MUTATION, {
    variables: {
      input: {
        email,
        password,
      },
    },
  });

  if (data?.customerAccessTokenCreate?.customerAccessToken?.accessToken) {
    return data.customerAccessTokenCreate.customerAccessToken.accessToken;
  }

  /**
   * Something is wrong with the user's input.
   */
  throw new Error(
    data?.customerAccessTokenCreate?.customerUserErrors.join(', '),
  );
}
